import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/supabase/supabaseClient";

/**
 * Hook to subscribe to real-time visit updates from Supabase
 * This will automatically update the visit cache when any visit is:
 * - Created (INSERT)
 * - Updated (UPDATE)
 * - Deleted (DELETE)
 */
export function useVisitsRealtime(taskId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!taskId) return;

    // Subscribe to changes on the visits table for this specific task
    const channel = supabase
      .channel(`visits-${taskId}-realtime`)
      .on(
        "postgres_changes",
        {
          event: "*", // Listen to all events (INSERT, UPDATE, DELETE)
          schema: "public",
          table: "visits",
          filter: `task_id=eq.${taskId}`,
        },
        (payload) => {
          console.log("Real-time visit update received:", payload);

          switch (payload.eventType) {
            case "INSERT":
            case "UPDATE":
            case "DELETE": {
              // Invalidate both visits and stats queries
              queryClient.invalidateQueries({
                queryKey: ["task-visits", taskId],
              });
              queryClient.invalidateQueries({
                queryKey: ["task-visit-stats", taskId],
              });
              console.log(`Visit ${payload.eventType.toLowerCase()}:`, payload);
              break;
            }
          }
        }
      )
      .subscribe((status) => {
        console.log(
          `Visit realtime subscription status for task ${taskId}:`,
          status
        );
      });

    // Cleanup subscription on unmount
    return () => {
      console.log(
        `Unsubscribing from visit realtime updates for task ${taskId}`
      );
      supabase.removeChannel(channel);
    };
  }, [queryClient, taskId]);
}

/**
 * Hook to subscribe to all visit updates (for admin views)
 */
export function useAllVisitsRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Subscribe to all changes on the visits table
    const channel = supabase
      .channel("all-visits-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "visits",
        },
        (payload) => {
          console.log("Real-time visit update received (all):", payload);

          // Invalidate all visit-related queries
          queryClient.invalidateQueries({
            queryKey: ["task-visits"],
          });
          queryClient.invalidateQueries({
            queryKey: ["task-visit-stats"],
          });
        }
      )
      .subscribe((status) => {
        console.log("All visits realtime subscription status:", status);
      });

    return () => {
      console.log("Unsubscribing from all visits realtime updates");
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
